/**
 * Rutas de marcación del realm staff (004 · US1 · T029; contracts/staff-vpp.md §1, §2 y §4).
 *
 * Las tres son **autenticadas** (wiring §0.5): el cliente añade la cookie `fermin_staff_session` y
 * la cabecera `X-Fermin-Device` firmada con `id=`. Aquí no se redefine ningún esquema: todos salen
 * de `@fermin/contracts`.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { z } from 'zod';

import {
  AttendanceStateResponseSchema,
  ChallengeCreatedResponseSchema,
  ChallengeQueuedResponseSchema,
  VerifyChallengeResponseSchema,
  WaitChallengeResponseSchema,
  type AttendanceStateResponse,
  type CreateChallengeBody,
  type VerifyChallengeBody,
  type VerifyChallengeResponse,
  type WaitChallengeResponse,
} from '@fermin/contracts';

import { api, type ApiError } from '../../api/client';

export const ATTENDANCE_STATE_QUERY_KEY = ['attendance-state'] as const;

/** `GET /attendance/state` — lectura pura; nunca responde `ATTENDANCE_STATE_UNRESOLVED`. */
export function fetchAttendanceState(): Promise<AttendanceStateResponse> {
  return api.get('/attendance/state', { schema: AttendanceStateResponseSchema });
}

/**
 * Alimenta el botón "MARCAR ENTRADA"/"MARCAR SALIDA" y la acción propuesta (FR-001).
 *
 * 006 · T028 (research R-16): `refetchOnWindowFocus: true` — al volver a la PWA (`visibilitychange`,
 * lo que dispara una PWA al primer plano) la home se refresca sola. El `QueryClient` global sigue
 * en `false` (`api/query-client.ts`); solo esta consulta y el historial lo activan.
 */
export function useAttendanceState(
  options: { enabled?: boolean } = {},
): UseQueryResult<AttendanceStateResponse, ApiError> {
  return useQuery<AttendanceStateResponse, ApiError>({
    queryKey: ATTENDANCE_STATE_QUERY_KEY,
    queryFn: fetchAttendanceState,
    enabled: options.enabled ?? true,
    // La acción propuesta se refresca al abrir la pantalla: no se sirve de caché vieja (FR-001).
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: true,
  });
}

/**
 * `POST /challenges`. El `201` (`PENDING`) y el `202` (`QUEUED`) se distinguen por el campo
 * `status`, que existe justo para eso: el cliente HTTP de Staff no expone el código.
 */
const CreateChallengeResultSchema = z.discriminatedUnion('status', [
  ChallengeCreatedResponseSchema,
  ChallengeQueuedResponseSchema,
]);

export type CreateChallengeResult = z.infer<typeof CreateChallengeResultSchema>;

export function createChallenge(body: CreateChallengeBody): Promise<CreateChallengeResult> {
  return api.post('/challenges', { body, schema: CreateChallengeResultSchema });
}

/** `POST /challenges/{id}/verify`. La clave de idempotencia se repite en los reintentos (FR-035a). */
export function verifyChallenge(
  challengeId: string,
  body: VerifyChallengeBody,
): Promise<VerifyChallengeResponse> {
  return api.post(`/challenges/${encodeURIComponent(challengeId)}/verify`, {
    body,
    schema: VerifyChallengeResponseSchema,
  });
}

/**
 * `POST /challenges/{id}/wait` — señal de espera en cola (004 · T048 · US4; FR-014, FR-014a).
 *
 * Es a la vez **latido** —su ausencia durante `queue_wait_heartbeat_timeout_ms` expira el challenge
 * y libera el turno— y **degradación del stream**: su respuesta lleva el mismo estado que habría
 * bajado por él, así que la marcación en curso no se pierde aunque el canal en vivo se caiga.
 *
 * El cuerpo es `{}` **presente pero vacío**, no ausente: el canon de `X-Fermin-Device` firma el
 * hash del cuerpo y el realm exige la cabecera anti-CSRF en toda mutación.
 *
 * Un `status` terminal distinto de `USED` no llega aquí: la API lo responde como error
 * (`410 CHALLENGE_EXPIRED` / `422 INSUFFICIENT_EVIDENCE`), que el llamador trata como cualquier otro
 * fallo del ciclo.
 */
export function waitChallenge(challengeId: string): Promise<WaitChallengeResponse> {
  return api.post(`/challenges/${encodeURIComponent(challengeId)}/wait`, {
    body: {},
    schema: WaitChallengeResponseSchema,
  });
}
